const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

/**
 * PUBLIC endpoint — deliberately NOT under /api and deliberately using plain
 * fetch rather than authenticatedFetch.
 *
 * This is the one documented exception to the repo's "always use
 * authenticatedFetch" rule (see CLAUDE.md): the caller is an external candidate
 * with no Google account and therefore no Firebase token, so there is nothing to
 * attach. Authorisation comes from the unguessable token in the link, which the
 * proxy validates. Every OTHER service in this app must keep using
 * authenticatedFetch.
 */
const BASE = `${PROXY_URL}/booking`;

export interface BookingSlot {
  start: string;
  end: string;
}

export interface ConfirmedBooking {
  /** False when our confirmation email could not be sent — the candidate then
   *  has no record of the interview, so the UI must say so. */
  emailSent?: boolean;
  start: string;
  end: string;
  meetLink?: string | null;
  htmlLink?: string | null;
  candidateEmail?: string;
  /**
   * One-click "add to Google Calendar" link for this booking, built server-side from the same
   * event the confirmation email's .ics describes (proxy: candidateCalendar). Absent on an
   * older proxy; null on a cancellation.
   */
  googleCalendarUrl?: string | null;
}

export interface BookingPage {
  token: string;
  jobTitle: string;
  stageName: string;
  minutes: number;
  timeZone: string;
  /** How many interviewers the candidate will meet — deliberately not WHO. */
  panelSize: number;
  /**
   * Whether a slot needs ONE of the team free (a round robin) or ALL of them (a panel). The
   * page said "everyone on the panel is free" for both until Sep 2026, which was never true of
   * the first kind — and first interviews are all of that kind.
   */
  meetingType?: '1:1' | 'panel';
  /**
   * The page's own sentences, from the Email Library, already narrowed to the ones that apply
   * (`lib/bookingCopy.js`). A null means TA has not written that one and the page keeps its
   * built-in English.
   */
  copy?: {
    meeting: string | null;
    bookedNote: string | null;
    change: string | null;
  } | null;
  /** The candidate's own name, from their invite — null for links without one. */
  candidateName?: string | null;
  lookaheadDays: number;
  minNoticeHours: number;
  /** True when the proxy is serving fake availability (no Google credentials). */
  fakeCalendar: boolean;
  /**
   * Which identity the proxy is using:
   *  - 'fake'      no calendar touched at all
   *  - 'bot-oauth' the non-human scheduling account (the real setup)
   *  - 'adc'       runtime service account: can only read shared calendars and
   *                cannot invite the candidate
   */
  calendarMode: 'fake' | 'bot-oauth' | 'adc';
  /** Set once the link has been used; slots are then empty. */
  booking: ConfirmedBooking | null;
  /**
   * May the candidate move this themselves? False for board-booked case and final rounds — the
   * picker cannot offer alternatives for those, so the affordance is withheld rather than
   * opening onto an empty list. Absent on an older proxy.
   */
  selfReschedule?: boolean;
  /**
   * Whether the page offers "None of these work? Suggest a time" — set per link by the recruiter.
   * Absent on an older proxy ⇒ not offered.
   */
  allowTimeSuggestions?: boolean;
  slots: BookingSlot[];
  /** Present when a panel calendar could not be read — no slots are offered. */
  slotError?: string | null;
}

/** Error carrying the proxy's message so the UI can show what actually failed. */
export class BookingError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'BookingError';
    this.status = status;
  }
}

async function readError(res: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const body = (await res.json()) as { error?: string };
    if (body?.error) message = body.error;
  } catch {
    // Non-JSON error body (proxy down, HTML error page) — keep the fallback.
  }
  throw new BookingError(message, res.status);
}

/**
 * The candidate's .ics for a booking — the same file the confirmation email attached, served
 * from the same builder, so importing it replaces rather than duplicates. A plain href, not a
 * fetch: the browser hands the file to whatever calendar app owns .ics.
 */
export const icsDownloadUrl = (token: string) => `${BASE}/${encodeURIComponent(token)}/calendar.ics`;

export async function fetchBookingPage(token: string): Promise<BookingPage> {
  const res = await fetch(`${BASE}/${encodeURIComponent(token)}`);
  if (!res.ok) await readError(res, 'Could not load this booking link');
  return res.json() as Promise<BookingPage>;
}

export async function confirmBooking(
  token: string,
  payload: { start: string; candidateName: string; candidateEmail: string }
): Promise<ConfirmedBooking> {
  const res = await fetch(`${BASE}/${encodeURIComponent(token)}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) await readError(res, 'Could not book this slot');
  const data = (await res.json()) as { booking: ConfirmedBooking; emailSent?: boolean };
  return { ...data.booking, emailSent: data.emailSent };
}

/**
 * "None of these work? Suggest a time." Books nothing: the proxy stores the day and time range
 * (Berlin time) and tells the recruiter, who answers the candidate. `date` is YYYY-MM-DD, `from`
 * and `to` are HH:MM.
 */
export async function suggestTime(
  token: string,
  payload: { date: string; from: string; to: string; note: string }
): Promise<void> {
  const res = await fetch(`${BASE}/${encodeURIComponent(token)}/suggest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) await readError(res, 'Could not send your suggestion');
}

/** An outreach booking ref (`r=` on an InMail's link): 12 URL-safe characters, else nothing. */
export const BOOKING_REF_RE = /^[A-Za-z0-9_-]{12}$/;

/** The confirm body: the ref only when well-formed. Pure. */
export function shareConfirmBody<T extends object>(payload: T & { ref?: string | null }): Omit<T, 'ref'> & { ref?: string } {
  const { ref, ...rest } = payload;
  return ref && BOOKING_REF_RE.test(ref) ? { ...rest, ref } : rest;
}

/**
 * The LinkedIn link's page (`/book/s/:shareToken`): the same meeting and slots, never a booking.
 * A refusal the prospect cannot act on — a closed link or role (410), an unknown link (404), or
 * today's bookings used up (429) — arrives as a BookingError like any other.
 */
const langQuery = (lang?: string | null) => (lang === 'de' || lang === 'en' ? `?lang=${lang}` : '');

export async function fetchSharePage(shareToken: string, lang?: string | null): Promise<BookingPage> {
  const res = await fetch(`${BASE}/s/${encodeURIComponent(shareToken)}${langQuery(lang)}`);
  if (!res.ok) await readError(res, 'Could not load this booking link');
  const page = (await res.json()) as BookingPage;
  return { ...page, token: page.token ?? '' };
}

/**
 * Book through the LinkedIn link. The proxy mints the prospect's OWN token for the booking and
 * returns it as `manageToken` — the page moves to that token's manage page, so reschedule and
 * cancel work exactly as they do from an invite. `ref`: the InMail's own `r=` (outreach, Nov 2 2026),
 * passed on only when it has the shape of one; the proxy ignores anything else.
 */
export async function confirmShareBooking(
  shareToken: string,
  payload: { start: string; candidateName: string; candidateEmail: string; linkedinUrl: string; ref?: string | null },
  lang?: string | null
): Promise<{ manageToken: string; booking: ConfirmedBooking }> {
  const res = await fetch(`${BASE}/s/${encodeURIComponent(shareToken)}/confirm${langQuery(lang)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(shareConfirmBody(payload)),
  });
  if (!res.ok) await readError(res, 'Could not book this slot');
  const data = (await res.json()) as { manageToken: string; booking: ConfirmedBooking; emailSent?: boolean };
  return { manageToken: data.manageToken, booking: { ...data.booking, emailSent: data.emailSent } };
}

/**
 * Move an existing booking to another free slot. The proxy re-checks
 * availability, so this can fail with 409 if the slot went in the meantime.
 */
export interface ReasonPayload {
  reasonCode: string;
  note: string;
}

export async function rescheduleBooking(
  token: string,
  start: string,
  reason: ReasonPayload
): Promise<ConfirmedBooking> {
  const res = await fetch(`${BASE}/${encodeURIComponent(token)}/reschedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ start, ...reason }),
  });
  if (!res.ok) await readError(res, 'Could not move the interview');
  const data = (await res.json()) as { booking: ConfirmedBooking; emailSent?: boolean };
  return { ...data.booking, emailSent: data.emailSent };
}

/**
 * Cancel the interview and free the link so a new time can be booked.
 * A reason is mandatory — it is the only signal the recruiter gets.
 */
export async function cancelBooking(token: string, reason: ReasonPayload): Promise<void> {
  const res = await fetch(`${BASE}/${encodeURIComponent(token)}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason),
  });
  if (!res.ok) await readError(res, 'Could not cancel the interview');
}
